// detailCapture.js
import { renderToStaticMarkup } from 'react-dom/server';
import React from 'react';
import MonthlyBreakdownPrint from './MonthlyBreakdownPrint';

// cheap, deterministic hash for small JSON payloads
function hashRecord(rec) {
  try {
    const payload = {
      oop: rec?.out_of_pocket_cost,          // drives most differences
      tier: rec?.tier_level_value,
      covered: rec?.covered,
      name: rec?.nonproprietaryname || rec?.proprietaryname || ''
    };
    const str = JSON.stringify(payload);
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const chr = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + chr;
      hash |= 0; // Convert to 32bit int
    }
    return hash.toString(36);
  } catch (e) {
    return Math.random().toString(36).slice(2, 8);
  }
}
 export function initDetailCache() { return {}; }

export function drugKeyFromRecord(rec) {
  let base = (rec.nonproprietaryname || rec.proprietaryname || rec.uniqueId || '').trim();
  if (!base) base = `drug-${Math.random().toString(36).slice(2)}`;
  return base.toLowerCase().replace(/\s+/g,'-');
}
/**
 * Extract PA JSX -> static HTML.
 * record.prior_authorization_details already present in `record`.
 * We replicate the JSX in renderRequirements *without* interactive bits.
 */
export function generatePaHtml(record) {
  const pa = record.prior_authorization_details || {};
  const jsx = (
    <div className="pa-block">
      {pa.error ? (
        <p><strong>Prior Authorization Error:</strong> {pa.error}</p>
      ) : (
        <>
          {pa.drug_name && <p><strong>Drug Name:</strong> {pa.drug_name}</p>}
          {pa.products_affected && <p><strong>Products Affected:</strong> {pa.products_affected}</p>}
          {pa.indications && <p><strong>Indications:</strong> {pa.indications}</p>}
          {pa.off_label_uses && <p><strong>Off-label Uses:</strong> {pa.off_label_uses}</p>}
          {pa.exclusion_criteria && <p><strong>Exclusion Criteria:</strong> {pa.exclusion_criteria}</p>}
          {pa.required_medical_information && <p><strong>Required Medical Information:</strong> {pa.required_medical_information}</p>}
          {pa.age_restrictions && <p><strong>Age Restrictions:</strong> {pa.age_restrictions}</p>}
            {pa.prescriber_restrictions && <p><strong>Prescriber Restrictions:</strong> {pa.prescriber_restrictions}</p>}
          {pa.coverage_duration && <p><strong>Coverage Duration:</strong> {pa.coverage_duration}</p>}
          {pa.other_criteria && <p><strong>Other Criteria:</strong> {pa.other_criteria}</p>}
          {pa.part_b_prerequisite && <p><strong>Part B Prerequisite:</strong> {pa.part_b_prerequisite}</p>}
          {(pa.pa_link || pa.st_link || pa.document_link) && (
            <div>
              <strong>Documents:</strong>
              <ul>
                {pa.pa_link && <li><a href={pa.pa_link} target="_blank" rel="noopener noreferrer">Prior Authorization PDF</a></li>}
                {pa.st_link && <li><a href={pa.st_link} target="_blank" rel="noopener noreferrer">Step Therapy PDF</a></li>}
                {pa.document_link && <li><a href={pa.document_link} target="_blank" rel="noopener noreferrer">Plan Documents</a></li>}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
  return renderToStaticMarkup(jsx);
}

/**
 * Render MonthlyBreakdown -> static HTML with a single record.
 * Assumes MonthlyBreakdown is pure enough for SSR. If it uses layout hooks that depend
 * on window, you may need guard code inside that component for print/SSR mode.
 */
export function generateMonthlyBreakdownHtml({
  record,
  selectedDaysSupply,
  selectedPharmacyType,
  selectedPlanDetails,
  drugCosts,
  startDate = new Date(),
  includeM3P = false,
  paymentPlanStartMonth,
}) {
  // Defensive: if record missing needed fields, return placeholder.
  if (!record) {
    return '<div class="monthly-breakdown-print-wrapper"><p>No data available for monthly breakdown.</p></div>';
  }

    const ppm = typeof paymentPlanStartMonth === 'number'
        ? paymentPlanStartMonth
        : (startDate instanceof Date ? startDate.getMonth() : 0);

  const jsx = (
    <div className="monthly-breakdown-print-wrapper">
      <MonthlyBreakdownPrint
        record={record}
        selectedPlanDetails={selectedPlanDetails}
        drugCosts={drugCosts}
        selectedDaysSupply={selectedDaysSupply}
        selectedPharmacyType={selectedPharmacyType}
        startDate={startDate}
        includeM3P={includeM3P}
        paymentPlanStartMonth={ppm}
      />
    </div>
  );
  return renderToStaticMarkup(jsx);
}

/**
 * Ensure cached PA / Breakdown HTML.
 */
export async function ensureDetailsForDrug({
  cache,
  setCache,
  record,
  needPa,
  needBreakdown,
  selectedDaysSupply,
  selectedPharmacyType,
  selectedPlanDetails,
  drugCosts,
  startDate,
  includeM3P = false,
  paymentPlanStartMonth
}) {
  const key = drugKeyFromRecord(record);
  let entry = cache[key] || {};
  let mutated = false;

  if (needPa && !entry.paHtml) {
    entry.paHtml = generatePaHtml(record);
    entry.paGenerated = new Date();
    mutated = true;
  }
  // allow multiple variants (M3P on/off, different start months)
  if (needBreakdown) {
    if (!entry.breakdowns) entry.breakdowns = {};
    const ppm = typeof paymentPlanStartMonth === 'number'
      ? paymentPlanStartMonth
      : (startDate instanceof Date ? startDate.getMonth() : 0);

    const variantKey = [
      `m3p:${includeM3P ? 1 : 0}`,
      `ppm:${ppm}`,
      `ds:${selectedDaysSupply}`,
      `pt:${selectedPharmacyType}`,
      `fid:${selectedPlanDetails?.formulary_id ?? ''}`,
      `pid:${selectedPlanDetails?.plan_id ?? ''}`,
      `cid:${selectedPlanDetails?.contract_id ?? ''}`,
      `seg:${selectedPlanDetails?.segment_id ?? ''}`,
      `ded:${selectedPlanDetails?.deductible ?? ''}`,
      `sd:${startDate instanceof Date ? startDate.toISOString().slice(0,10) : startDate}`,
      `rec:${hashRecord(record)}`
    ].join('|');

    if (!entry.breakdowns[variantKey]) {
      entry.breakdowns[variantKey] = generateMonthlyBreakdownHtml({
        record,
        selectedDaysSupply,
        selectedPharmacyType,
        selectedPlanDetails,
        drugCosts,
        startDate,
        includeM3P,
        paymentPlanStartMonth
      });
      entry.breakdownGenerated = new Date();
      mutated = true;
    }
    // expose the one we just built (or existing) under a stable name for callers
    entry.monthlyBreakdownHtml = entry.breakdowns[variantKey];
  }
  if (mutated) {
    setCache(prev => ({ ...prev, [key]: entry }));
  }
  return { key, ...entry };
}
